import Echo from 'laravel-echo'
import Pusher from 'pusher-js'

let echoInstance = null

function readEnv(name, fallback) {
    const v = process.env[name]
    return v === undefined || v === '' ? fallback : v
}

function readBoolEnv(name, fallback) {
    const v = process.env[name]
    if (v === undefined || v === '') return fallback
    return v === 'true' || v === '1'
}

function readIntEnv(name, fallback) {
    const v = process.env[name]
    if (v === undefined || v === '') return fallback
    const parsed = parseInt(v, 10)
    return Number.isFinite(parsed) ? parsed : fallback
}

function getCurrentToken() {
    // Mirrors the axios interceptor in config/backend.js so an impersonation
    // bearer takes precedence over the regular one.
    return localStorage.getItem('token_impersonate') || localStorage.getItem('token')
}

function buildAuthEndpoint() {
    const explicit = readEnv('VUE_APP_PUSHER_AUTH_ENDPOINT', '')
    if (explicit) return explicit
    const backend = readEnv('VUE_APP_BACKEND_URL', '').replace(/\/$/, '')
    return backend ? `${backend}/broadcasting/auth` : '/broadcasting/auth'
}

export function getEcho() {
    if (echoInstance) return echoInstance

    const key = readEnv('VUE_APP_PUSHER_KEY', '')
    if (!key) {
        console.warn('[echo] VUE_APP_PUSHER_KEY is not set — realtime notifications disabled.')
        return null
    }

    // Echo internally does `new window.Pusher(...)` when broadcaster='pusher'.
    if (typeof window !== 'undefined') {
        window.Pusher = Pusher
    }

    const wsHost = readEnv('VUE_APP_PUSHER_HOST', '')
    const wsPort = readIntEnv('VUE_APP_PUSHER_PORT', 6001)
    const forceTLS = readBoolEnv('VUE_APP_PUSHER_FORCE_TLS', false)
    const authEndpoint = buildAuthEndpoint()

    const config = {
        broadcaster: 'pusher',
        key,
        cluster: readEnv('VUE_APP_PUSHER_CLUSTER', 'mt1'),
        forceTLS,
        disableStats: readBoolEnv('VUE_APP_PUSHER_DISABLE_STATS', true),
        enabledTransports: ['ws', 'wss'],
        authEndpoint,
        // Custom authorizer (vs static `auth.headers`) so the bearer is read
        // fresh on every channel subscription — login refresh / impersonation
        // takes effect on the next subscribe without reconnecting the socket.
        authorizer: (channel) => ({
            authorize(socketId, callback) {
                const token = getCurrentToken()
                fetch(authEndpoint, {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: 'application/json',
                        ...(token ? { Authorization: `Bearer ${token}` } : {})
                    },
                    body: JSON.stringify({
                        socket_id: socketId,
                        channel_name: channel.name
                    })
                })
                    .then((res) => {
                        if (!res.ok) throw new Error(`broadcasting/auth ${res.status}`)
                        return res.json()
                    })
                    .then((data) => callback(null, data))
                    .catch((err) => callback(err, null))
            }
        })
    }

    // Self-hosted (Reverb / Soketi): point at the local WS server.
    // Leave VUE_APP_PUSHER_HOST empty to talk to Pusher SaaS via the cluster.
    if (wsHost) {
        config.wsHost = wsHost
        config.wsPort = wsPort
        config.wssPort = wsPort
    }

    echoInstance = new Echo(config)

    // Surface as window.Echo for debugging (matches Laravel docs convention).
    if (typeof window !== 'undefined') {
        window.Echo = echoInstance
    }

    return echoInstance
}

export function disconnectEcho() {
    if (!echoInstance) return
    try {
        echoInstance.disconnect()
    } catch (e) {
        // noop — instance may already be torn down
    }
    echoInstance = null
    if (typeof window !== 'undefined') {
        window.Echo = null
    }
}

export default getEcho
